import React, { useCallback } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import { Code, Visibility } from '@mui/icons-material';
import styled from 'styled-components';

type SvgIconComponent = typeof SvgIcon;

export type ActiveIcon = 'hcs' | 'json' | 'dial';

const StyledPreviewIconsBar = styled.div`
  position: absolute;
  top: 0;
  right: 0;

  z-index: 100;

  .MuiIconButton-root {
    padding: 0.4rem;
  }
`;

interface IconBarItemProps {
  Icon: SvgIconComponent;
  active?: boolean;
  tooltip: string;
  ariaLabel: string;
  onClick?: () => void;
  disabled?: boolean;
}

const IconBarItem: React.FC<IconBarItemProps> = ({
  Icon,
  active = false,
  tooltip,
  ariaLabel,
  onClick,
  disabled = false,
}) => {
  const color = active ? 'primary' : 'disabled';

  return (
    <Tooltip title={<span style={{ fontSize: '16px' }}>{tooltip}</span>} placement="left">
      <span>
        <IconButton aria-label={ariaLabel} onClick={onClick} disabled={disabled}>
          <Icon fontSize="small" color={color} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

interface PreviewIconsBarProps {
  activeIcon: ActiveIcon;
  onChange?: (icon: ActiveIcon) => void;
  jsonDisabled: boolean;
}

const PreviewIconsBar: React.FC<PreviewIconsBarProps> = ({ activeIcon, onChange, jsonDisabled }) => {
  const handleOnClick = useCallback(
    (activeIcon: ActiveIcon) => {
      if (onChange !== undefined) {
        onChange(activeIcon);
      }
    },
    [onChange],
  );

  return (
    <StyledPreviewIconsBar>
      <IconBarItem
        Icon={Visibility}
        active={activeIcon === 'hcs'}
        tooltip="HCS Preview"
        ariaLabel="hcs-preview"
        onClick={() => handleOnClick('hcs')}
      />
      <IconBarItem
        Icon={Code}
        active={activeIcon === 'json'}
        tooltip={jsonDisabled ? 'Please complete all required fields (*)' : 'JSON Preview'}
        ariaLabel="json-preview"
        onClick={() => handleOnClick('json')}
        disabled={jsonDisabled}
      />
    </StyledPreviewIconsBar>
  );
};

export default PreviewIconsBar;
